import apiClient from './api';
import { getLanguage, t } from '../i18n';

/** Friendly message for the common error statuses the vendor dashboard
 * hits (CLAUDE.md D16 part B item 4) -- prefers the backend's own detail
 * when present, falls back to a plain-language explanation otherwise. */
export function friendlyApiError(error, fallback) {
  const status = error.response?.status;
  // A 422 sends `detail` as an array of objects: never hand that to the UI (React can't render it).
  const raw = error.response?.data?.detail;
  const detail = typeof raw === 'string' ? raw : undefined;
  if (status === 403) return detail || t('apiErrors.forbidden');
  if (status === 409) return detail || t('apiErrors.conflict');
  if (status === 400) return detail || t('apiErrors.badInput');
  // R9: the per-call English fallbacks stay for English; Urdu gets one plain message.
  // (Server-sent `detail` text is not localized yet.)
  return detail || (getLanguage() === 'ur' ? t('apiErrors.generic') : fallback);
}

export class MeService {
  static async getEntitlements() {
    try {
      const response = await apiClient.get('/me/entitlements');
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your plan access') };
    }
  }
}

export class TaxonomyService {
  static async getCategories() {
    try {
      const response = await apiClient.get('/categories');
      return { success: true, categories: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load categories') };
    }
  }

  static async getBrands() {
    try {
      const response = await apiClient.get('/brands');
      return { success: true, brands: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load brands') };
    }
  }
}

export class VendorProductsService {
  static async list(params = {}) {
    try {
      const response = await apiClient.get('/vendor/products', { params });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your products') };
    }
  }

  static async get(id) {
    try {
      const response = await apiClient.get(`/vendor/products/${id}`);
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load this product') };
    }
  }

  static async create(payload) {
    try {
      const response = await apiClient.post('/vendor/products', payload);
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to create product') };
    }
  }

  static async update(id, payload) {
    try {
      const response = await apiClient.put(`/vendor/products/${id}`, payload);
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to update product') };
    }
  }

  static async togglePublished(id, isPublished) {
    try {
      const response = await apiClient.post(`/vendor/products/${id}/publish`, null, {
        params: { is_published: isPublished },
      });
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to update product') };
    }
  }

  static async remove(id) {
    try {
      const response = await apiClient.delete(`/vendor/products/${id}`);
      return { success: true, product: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to remove product') };
    }
  }

  static async uploadImage(file, { transparent = false } = {}) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      // R8: a try-on overlay must have a see-through background (checked server-side too).
      const url = transparent ? '/vendor/products/images?transparent=true' : '/vendor/products/images';
      const response = await apiClient.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, url: response.data?.url };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to upload image') };
    }
  }
}

export class VendorInventoryService {
  static async list() {
    try {
      const response = await apiClient.get('/vendor/inventory');
      return { success: true, items: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load stock') };
    }
  }

  static async update(productId, stockQuantity) {
    try {
      const response = await apiClient.put(`/vendor/inventory/${productId}`, { stock_quantity: stockQuantity });
      return { success: true, item: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to update stock') };
    }
  }
}

export class VendorOrdersService {
  static async list(params = {}) {
    try {
      const response = await apiClient.get('/vendor/orders', { params });
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load orders') };
    }
  }

  static async updateStatus(vendorOrderId, { status, carrier, trackingNumber, scheduledAt } = {}) {
    try {
      const response = await apiClient.put(`/vendor/vendor-orders/${vendorOrderId}/status`, {
        status,
        carrier: carrier || undefined,
        tracking_number: trackingNumber || undefined,
        scheduled_at: scheduledAt || undefined,
      });
      return { success: true, vendorOrder: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to update order status') };
    }
  }

  static async cancel(vendorOrderId, reason) {
    try {
      const response = await apiClient.post(`/vendor/vendor-orders/${vendorOrderId}/cancel`, { reason });
      return { success: true, vendorOrder: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to cancel order') };
    }
  }

  static async exportCsv(params = {}) {
    try {
      const response = await apiClient.get('/vendor/orders/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'my-orders.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to export orders') };
    }
  }

  static async confirmPayment(vendorOrderId) {
    try {
      const response = await apiClient.post(`/vendor/vendor-orders/${vendorOrderId}/confirm-payment`);
      return { success: true, vendorOrder: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to confirm payment') };
    }
  }

  static async rejectPayment(vendorOrderId, reason) {
    try {
      const response = await apiClient.post(`/vendor/vendor-orders/${vendorOrderId}/reject-payment`, { reason });
      return { success: true, vendorOrder: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to reject payment') };
    }
  }
}

export class VendorAnalyticsService {
  static async get(params = {}) {
    try {
      const response = await apiClient.get('/vendor/analytics', { params });
      return { success: true, analytics: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load analytics') };
    }
  }
}

export class VendorStoreService {
  static async get() {
    try {
      const response = await apiClient.get('/vendor/store');
      return { success: true, store: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load store settings') };
    }
  }

  static async update(payload) {
    try {
      const response = await apiClient.put('/vendor/store', payload);
      return { success: true, store: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to update store settings') };
    }
  }
}

export class VendorBillingService {
  static async getContract() {
    try {
      const response = await apiClient.get('/vendor/contract');
      return { success: true, contract: response.data };
    } catch (error) {
      if (error.response?.status === 404) return { success: true, contract: null };
      return { success: false, error: friendlyApiError(error, 'Failed to load your contract') };
    }
  }

  static async getCommissionLedger() {
    try {
      const response = await apiClient.get('/vendor/billing/commission/ledger');
      return { success: true, entries: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your commission ledger') };
    }
  }

  static async getCommissionSummary(period) {
    try {
      const response = await apiClient.get('/vendor/billing/commission/summary', { params: { period } });
      return { success: true, summary: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your commission summary') };
    }
  }

  static async listInvoices() {
    try {
      const response = await apiClient.get('/vendor/billing/invoices');
      return { success: true, invoices: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load invoices') };
    }
  }

  static async getState() {
    try {
      const response = await apiClient.get('/vendor/billing/state');
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load billing state') };
    }
  }

  static async uploadProof({ invoiceId, method, amount, reference, file }) {
    try {
      const formData = new FormData();
      formData.append('invoice_id', invoiceId);
      formData.append('method', method);
      formData.append('amount', amount);
      if (reference) formData.append('reference', reference);
      formData.append('file', file);
      const response = await apiClient.post('/vendor/billing/proof', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { success: true, proof: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to upload payment proof') };
    }
  }

  static async getStatement(params = {}) {
    try {
      const response = await apiClient.get('/vendor/billing/statement', { params });
      return { success: true, statement: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your statement') };
    }
  }
}

export class VendorTaxonomyService {
  static async listRequests() {
    try {
      const response = await apiClient.get('/vendor/taxonomy-requests');
      return { success: true, requests: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your requests') };
    }
  }

  static async createRequest(payload) {
    try {
      const response = await apiClient.post('/vendor/taxonomy-requests', payload);
      return { success: true, request: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to submit request') };
    }
  }

  // R2.3 store-scoped taxonomy
  static async getMyCategories() {
    try {
      const response = await apiClient.get('/vendor/categories');
      return { success: true, categories: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load categories') };
    }
  }

  static async getMyBrands() {
    try {
      const response = await apiClient.get('/vendor/brands');
      return { success: true, brands: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load brands') };
    }
  }

  static async createBrand(name) {
    try {
      const response = await apiClient.post('/vendor/brands', { name });
      return { success: true, brand: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to create brand') };
    }
  }

  static async requestGlobalBrand(brandId) {
    try {
      const response = await apiClient.post(`/vendor/brands/${brandId}/request-global`);
      return { success: true, brand: response.data };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to request approval') };
    }
  }

  static async getStoreCategoryIds() {
    try {
      const response = await apiClient.get('/vendor/store/categories');
      return { success: true, categoryIds: response.data.category_ids };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to load your categories') };
    }
  }

  static async setStoreCategoryIds(categoryIds) {
    try {
      const response = await apiClient.put('/vendor/store/categories', { category_ids: categoryIds });
      return { success: true, categoryIds: response.data.category_ids };
    } catch (error) {
      return { success: false, error: friendlyApiError(error, 'Failed to save your categories') };
    }
  }
}
